import { createSlice } from "@reduxjs/toolkit";
import { AibleClientFactory } from "client/aible/factory";
import * as accountsReducer from "containers/Accounts/reducer";
import { getNumericComparator, reverseOrder } from "@/utils/collect";
const clientFactory = AibleClientFactory.getInstance();
const client = clientFactory.getContentGenerationClient();
function updateStateWithConnection(state, action) {
    return {
        ...state,
        connections: {
            ...state.connections,
            [action.id]: action
        },
        models: updateModelsStateWithModels(state.models, action.models)
    };
}
function deleteConnectionFromState(state, id) {
    const { [id]: deletedConnection, ...connections } = state.connections;
    if (deletedConnection === undefined) {
        return state;
    }
    return deletedConnection.models.reduce(deleteModelFromState, {
        ...state,
        connections: connections
    });
}
function updateStateWithConnectionQuery(state, { query, ids }) {
    const queryStr = JSON.stringify(query);
    return {
        ...state,
        queries: {
            ...state.queries,
            connections: {
                ...state.queries.connections,
                [queryStr]: ids
            }
        }
    };
}
function updateStateWithModel(state, action) {
    return {
        ...state,
        models: updateModelsStateWithModels(state.models, [action])
    };
}
function updateModelsStateWithModels(state, action) {
    const update = {
        CONTENT_GENERATION: { ...state["CONTENT_GENERATION"] },
        EMBEDDING: { ...state["EMBEDDING"] },
        RERANKING: { ...state["RERANKING"] }
    };
    for (const model of action) {
        update[model.type][model.id] = model;
    }
    return update;
}
function deleteModelFromState(state, model) {
    const { [model.id]: _, ...models } = state.models[model.type];
    return {
        ...state,
        models: {
            ...state.models,
            [model.type]: models
        }
    };
}
const initialState = {
    connections: {},
    models: {
        CONTENT_GENERATION: {},
        EMBEDDING: {},
        RERANKING: {}
    },
    queries: {
        connections: {},
        models: {}
    }
};
const aiServiceSlice = createSlice({
    name: "aiService",
    initialState,
    reducers: {
        // Connections
        updateConnection(state, { payload }) {
            return updateStateWithConnection(state, payload);
        },
        updateConnections(state, { payload }) {
            return payload.reduce(updateStateWithConnection, state);
        },
        deleteConnection(state, { payload }) {
            return deleteConnectionFromState(state, payload);
        },
        // ConnectionQuery
        updateConnectionQuery(state, { payload }) {
            return updateStateWithConnectionQuery(state, payload);
        },
        // Models
        updateModel(state, { payload }) {
            return updateStateWithModel(state, payload);
        },
        updateModels(state, { payload }) {
            return payload.reduce(updateStateWithModel, state);
        },
        deleteModel(state, { payload }) {
            return deleteModelFromState(state, payload);
        }
    }
});
export default aiServiceSlice.reducer;
function getBase(state) {
    return state.aiService;
}
export function getConnections(state) {
    return getBase(state).connections;
}
export function getConnectionById(state, id) {
    return getBase(state).connections[id];
}
export function getConnectionIdsByQuery(state, query) {
    const queryStr = JSON.stringify(query);
    return getBase(state).queries.connections[queryStr];
}
// WARNING: DO NOT create a selector and use Object.anything as it creates a new
// object and will force a rerender
export function getModels(state, type = "CONTENT_GENERATION") {
    return getBase(state).models[type];
}
export function getModelById(state, id, type = "CONTENT_GENERATION") {
    return getBase(state).models[type][id];
}
export function getModelByExternalId(state, externalId) {
    const models = Object.values(getModels(state));
    for (const model of models) {
        if (model.external_id == externalId) {
            return model;
        }
    }
    return undefined;
}
export function getServiceIdByModelId(state, modelId, type = "CONTENT_GENERATION") {
    if (modelId === undefined) {
        return undefined;
    }
    return getModelById(state, modelId, type)?.id;
}
export function createConnection(accountId, post) {
    return async (dispatch) => {
        const cloudClient = (await clientFactory.getAccountClient().getCustomerClientFactory(accountId)).getContentGenerationClient();
        const connection = await cloudClient.createConnectionWithConfig(post);
        dispatch(aiServiceSlice.actions.updateConnection(connection));
        return connection;
    };
}
export function handleCreateConnection(accountId, name, type, config, folder_id) {
    return (dispatch) => {
        const newConnectionProps = {
            account_id: accountId,
            name: name,
            type: type,
            status: "ENABLED",
            metadata: {},
            config
        };
        if (folder_id !== undefined) {
            newConnectionProps["folder_id"] = folder_id;
        }
        return dispatch(createConnection(accountId, newConnectionProps));
    };
}
export function updateConnection(id, post) {
    return async (dispatch) => {
        const connection = await client.updateConnection(id, post);
        dispatch(aiServiceSlice.actions.updateConnection(connection));
        return connection;
    };
}
export function refreshModelsList() {
    return async (dispatch, getState) => {
        try {
            const AccountClient = AibleClientFactory.getInstance().getAccountClient();
            const accounts = accountsReducer.getAccounts(getState());
            const promises = accounts.map((acc) => {
                return AccountClient.getAccountClient(acc)
                    .then((client) => {
                    return client.get("v1/ai_service_models/reload").catch((e) => {
                        console.log(e);
                    });
                })
                    .catch((e) => {
                    console.log(e);
                });
            });
            await Promise.all(promises).finally(() => {
                const selectedAccount = accountsReducer.getSelectedAccountId(getState());
                return dispatch(fetchAll({
                    account_id: selectedAccount
                }));
            });
        }
        catch (e) {
            console.log(e);
        }
        return Promise.resolve();
    };
}
export function fetchConnections(query) {
    return async (dispatch) => {
        const connections = await client.getConnections(query);
        dispatch(aiServiceSlice.actions.updateConnections(connections));
        // Save the query along with the ids for lookup later.
        const ids = connections.map((connection) => connection.id);
        dispatch(aiServiceSlice.actions.updateConnectionQuery({ query, ids }));
        return connections;
    };
}
export function fetchConnectionById(id) {
    return async (dispatch) => {
        const connection = await client.getConnectionById(id);
        dispatch(aiServiceSlice.actions.updateConnection(connection));
        return connection;
    };
}
export function fetchConnectionModels(id) {
    return async (dispatch) => {
        const models = await client.getConnectionModels(id);
        dispatch(aiServiceSlice.actions.updateModels(models));
        return models;
    };
}
export function deleteConnection(accountId, id) {
    return async (dispatch) => {
        const cloudClient = (await clientFactory.getAccountClient().getCustomerClientFactory(accountId)).getContentGenerationClient();
        const connection = await cloudClient.deleteConnection(id);
        if (connection.deleted === "DELETED") {
            dispatch(aiServiceSlice.actions.deleteConnection(id));
        }
    };
}
export function handleDeleteConnection(accountId, id) {
    return (dispatch) => {
        const shouldDelete = confirm(`This will delete the connection. This will also remove all the models associated with this connection.`);
        if (shouldDelete) {
            return dispatch(deleteConnection(accountId, id));
        }
        else {
            return Promise.resolve();
        }
    };
}
export function createModel(connection, post) {
    return (dispatch) => {
        return clientFactory
            .getAccountClient()
            .getCustomerClientFactory(connection.account_id)
            .then((factory) => factory.getContentGenerationClient())
            .then((client) => client.createModelWithConfig(connection.id, post))
            .then((model) => {
            dispatch(aiServiceSlice.actions.updateModel(model));
            return model;
        });
    };
}
export function handleCreateModel(connection, name, modelId, type, config) {
    return (dispatch) => {
        const newModelParams = {
            name: name,
            external_id: modelId,
            type: type,
            status: "ENABLED",
            metadata: {},
            config
        };
        return dispatch(createModel(connection, newModelParams));
    };
}
export function updateModel(id, post) {
    return async (dispatch) => {
        const Model = await client.updateModel(id, post);
        dispatch(aiServiceSlice.actions.updateModel(Model));
        return Model;
    };
}
export function fetchModels(query) {
    return async (dispatch) => {
        const models = await client.getModels(query);
        dispatch(aiServiceSlice.actions.updateModels(models));
        return models;
    };
}
export function fetchModelById(id) {
    return async (dispatch) => {
        const model = await client.getModelById(id);
        dispatch(aiServiceSlice.actions.updateModel(model));
        return model;
    };
}
export function updateModelStatus(id, status) {
    return async (dispatch) => {
        const model = await clientFactory.getContentGenerationClient().updateModel(id, {
            status
        });
        dispatch(aiServiceSlice.actions.updateModel(model));
    };
}
export function disableModel(id) {
    return updateModelStatus(id, "DISABLED");
}
export function enableModel(id) {
    return updateModelStatus(id, "ENABLED");
}
export function deleteModel(accountId, id) {
    return async (dispatch) => {
        // Delete the model and configuration.
        const cloudClient = (await clientFactory.getAccountClient().getCustomerClientFactory(accountId)).getContentGenerationClient();
        const model = await cloudClient.deleteModel(id);
        if (model.deleted === "DELETED") {
            dispatch(aiServiceSlice.actions.deleteModel(model));
        }
    };
}
export function handleDeleteModel(accountId, id) {
    return (dispatch) => {
        const shouldDelete = confirm(`This will delete the Provisioned Model. If you still want to use this model, you will have to provision it again.`);
        if (shouldDelete) {
            return dispatch(deleteModel(accountId, id));
        }
        else {
            return Promise.resolve();
        }
    };
}
export function fetchAll(query) {
    return async (dispatch) => {
        const connections = dispatch(fetchConnections(query));
        const models = dispatch(fetchModels(query));
        await Promise.all([connections, models]);
    };
}
export function getAiServiceModelList(models, withStatus) {
    const values = Object.values(models);
    if (withStatus === undefined) {
        return values;
    }
    const statusSet = new Set(withStatus);
    return values.filter((model) => statusSet.has(model.status));
}
export function getInProgressAiServiceModels(models) {
    return getAiServiceModelList(models, ["PROVISIONING"]);
}
export function checkModelStatus(modelId) {
    return async (dispatch, getState) => {
        const state = getState();
        const model = getModelById(state, modelId);
        if (model === undefined) {
            throw new Error("Model was undefined. Possibly not loaded?");
        }
        const connection = getConnectionById(state, model.connection_id);
        if (connection === undefined) {
            throw new Error("Connection was undefined. Possibly not loaded?");
        }
        const accountId = connection.account_id;
        const accountClient = AibleClientFactory.getInstance().getAccountClient();
        const customerClient = await accountClient.getCustomerClientFactory(accountId);
        return customerClient.getContentGenerationClient().getModelStatus(modelId);
    };
}
export function modelCreationTimeMillis(model) {
    return new Date(model.created_date).getTime();
}
function isRelativelyNew(model) {
    const timeMillis = modelCreationTimeMillis(model);
    const thirtyDaysAgoMillis = Date.now() - 30 * 1000 * 60 * 60 * 24;
    return timeMillis > thirtyDaysAgoMillis;
}
export function pollAiServiceModelStatus(max_poll = 5) {
    return async (dispatch, getState) => {
        const state = getState();
        const models = getInProgressAiServiceModels(getModels(state));
        const connections = getConnections(state);
        const virtualConnections = new Set([
            "AZURE_OPEN_AI",
            "OPEN_AI",
            "VERTEX_AI_GEMINI",
            "VERTEX_AI"
        ]);
        const targetModels = models
            .filter((model) => 
        // We don't want to match virtual connections that can't have their statuses updated.
        !virtualConnections.has(connections[model.connection_id].type) && isRelativelyNew(model))
            .map((model) => [model, modelCreationTimeMillis(model)])
            .toSorted(reverseOrder(getNumericComparator((pair) => pair[1])))
            .map((pair) => pair[0]);
        return Promise.allSettled(Iterator.from(targetModels)
            .take(max_poll)
            .map((model) => dispatch(checkModelStatus(model.id)))).then(() => {
            if (targetModels.length > 0) {
                dispatch(fetchModels({}));
            }
        });
    };
}
